import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Truck, PackageCheck, Store, AlertTriangle, Loader2, Send, FileDown, Eye, Printer, CheckCircle2, Plus, Trash2, Package,
} from "lucide-react";

const dims = (d) => {
  if (!d) return "—";
  const parts = [d.length, d.width, d.height].map((v) => (v ? Math.round(v) : "—"));
  return `${parts[0]} × ${parts[1]} × ${parts[2]} cm`;
};

const ZONE_SOURCE_LABEL = {
  woocommerce_zone_instance: "WooCommerce zone (method)",
  woocommerce_zone_postcode: "WooCommerce zone (postcode)",
  manual_zone: "OPPS zone (fallback)",
  derived_rule: "Derived from rule",
  fallback: "Fallback (General UK)",
};

const WEIGHT_OPTIONS = [0.5, 1, 2, 5, 10, 15, 20, 25, 30];

const Kv = ({ k, v, strong, mono }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{k}</div>
    <div className={`${strong ? "font-bold text-slate-900" : "text-slate-700"} ${mono ? "font-mono" : ""}`}>{v || "—"}</div>
  </div>
);

export const ShippingResolutionCard = ({ legacyId }) => {
  const { user, isSuper, can } = useAuth();
  const canManage = isSuper || can("orders.manage") || can("deliveries.manage") || can("operations.manage") || can("settings.manage");
  const [state, setState] = useState({ loading: true });
  const [shipment, setShipment] = useState(null);
  const [busy, setBusy] = useState(null); // 'create' | 'label' | 'pdf' | 'weight'
  const [customOpen, setCustomOpen] = useState(false);
  const [customW, setCustomW] = useState("");
  const [parcels, setParcels] = useState([{ weight_kg: "", length: "", width: "", height: "" }]);
  const [parcelsInit, setParcelsInit] = useState(false);

  const loadShipment = useCallback(() => {
    return api.get(`/royalmail/orders/${legacyId}`).then((r) => setShipment(r.data.shipment)).catch(() => {});
  }, [legacyId]);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get(`/shipping/resolve/${legacyId}`).then((r) => alive && setState({ loading: false, ...r.data })),
      loadShipment(),
    ]).catch((e) => alive && setState({ loading: false, error: e.response?.data?.detail || "Could not resolve shipping" }));
    return () => { alive = false; };
  }, [legacyId, loadShipment]);

  useEffect(() => {
    if (parcelsInit || !state.fulfilment) return;
    const w = state.fulfilment.manual_parcel_weight_kg ?? state.fulfilment.parcel_weight_kg;
    const d = state.fulfilment.parcel_dimensions_cm || {};
    setParcels([{ weight_kg: w ?? "", length: d.length ?? "", width: d.width ?? "", height: d.height ?? "" }]);
    setParcelsInit(true);
  }, [state.fulfilment, parcelsInit]);

  const setWeight = async (val) => {
    setBusy("weight");
    try {
      const r = await api.put(`/shipping/orders/${legacyId}/weight`, { weight_kg: val });
      setState((s) => ({ ...s, fulfilment: r.data.fulfilment, resolution: r.data.resolution }));
      toast.success(val == null ? "Weight cleared" : `Parcel weight set to ${val} kg`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not set weight"); }
    finally { setBusy(null); }
  };

  const cleanParcels = () => parcels
    .map((p) => ({ weight_kg: Number(p.weight_kg), length: p.length ? Number(p.length) : null, width: p.width ? Number(p.width) : null, height: p.height ? Number(p.height) : null }))
    .filter((p) => p.weight_kg > 0);
  const [preview, setPreview] = useState(null);
  const checkRequest = async () => {
    const clean = cleanParcels();
    if (clean.length === 0) { toast.error("Add at least one parcel with a weight."); return; }
    setBusy("preview");
    try { const r = await api.post(`/royalmail/orders/${legacyId}/preview`, { parcels: clean }); setPreview(r.data.request); }
    catch (e) { setPreview(null); toast.error(e.response?.data?.detail || "Request is not valid"); }
    finally { setBusy(null); }
  };
  const createOrder = async () => {
    const clean = cleanParcels();
    if (clean.length === 0) { toast.error("Add at least one parcel with a weight."); return; }
    setBusy("create");
    try {
      const r = await api.post(`/royalmail/orders/${legacyId}`, { parcels: clean });
      setShipment(r.data.shipment);
      toast.success(r.data.idempotent ? "Royal Mail order already exists" : `Royal Mail order created · ${clean.length} parcel${clean.length > 1 ? "s" : ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create Royal Mail order"); await loadShipment(); }
    finally { setBusy(null); }
  };
  const addParcel = () => setParcels((ps) => [...ps, { weight_kg: "", length: "", width: "", height: "" }]);
  const removeParcel = (i) => setParcels((ps) => ps.length > 1 ? ps.filter((_, idx) => idx !== i) : ps);
  const updateParcel = (i, k, v) => setParcels((ps) => ps.map((p, idx) => idx === i ? { ...p, [k]: v } : p));

  const pullLabel = async () => {
    setBusy("label");
    try {
      const r = await api.post(`/royalmail/orders/${legacyId}/label`);
      setShipment(r.data.shipment);
      toast.success("Label retrieved and stored");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not retrieve label"); await loadShipment(); }
    finally { setBusy(null); }
  };

  const withLabelBlob = async (fn) => {
    setBusy("pdf");
    try {
      const r = await api.get(`/royalmail/orders/${legacyId}/label.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      fn(url);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not open label"); }
    finally { setBusy(null); }
  };
  const viewLabel = () => withLabelBlob((url) => window.open(url, "_blank"));
  const downloadLabel = () => withLabelBlob((url) => {
    const a = document.createElement("a"); a.href = url; a.download = `royalmail-label-${legacyId}.pdf`; a.click();
  });
  const printLabel = () => withLabelBlob((url) => {
    const w = window.open(url, "_blank");
    if (w) w.addEventListener("load", () => { try { w.print(); } catch (_) {} });
  });
  const printParcel = (n) => withLabelBlob((url) => {
    const w = window.open(`${url}#page=${n}`, "_blank");
    if (w) w.addEventListener("load", () => { try { w.print(); } catch (_) {} });
  });

  if (state.loading) {
    return <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 flex items-center gap-2 text-sm text-slate-500" data-testid="shipping-resolution-card"><Loader2 className="w-4 h-4 animate-spin" /> Resolving fulfilment…</section>;
  }
  if (state.error) {
    return <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 text-sm text-slate-500" data-testid="shipping-resolution-card">{state.error}</section>;
  }

  const f = state.fulfilment || {};
  const res = state.resolution || {};
  const status = res.status || (res.click_collect ? "no_shipment" : res.resolved ? "resolved" : "manual_review");
  const resolved = status === "resolved";
  const clickCollect = status === "no_shipment";

  const BADGES = {
    resolved: { icon: PackageCheck, cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Rule matched" },
    no_shipment: { icon: Store, cls: "bg-violet-50 text-violet-700 border-violet-200", label: "Click & Collect" },
    manual_review: { icon: AlertTriangle, cls: "bg-amber-50 text-amber-700 border-amber-200", label: "Manual review" },
  };
  const badge = BADGES[status] || BADGES.manual_review;
  const BIcon = badge.icon;
  const hasRmOrder = shipment && shipment.rm_order_identifier;
  const labelStored = shipment && shipment.label_status === "stored";

  return (
    <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="shipping-resolution-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center"><Truck className="w-4 h-4" /></span>
          <h2 className="text-base font-bold text-slate-900">Fulfilment &amp; shipping</h2>
        </div>
        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${badge.cls}`} data-testid="shipping-resolution-badge">
          <BIcon className="w-3.5 h-3.5" /> {badge.label}
        </span>
      </div>

      <div className="rounded-xl bg-slate-50 border border-slate-100 p-4 mb-4">
        {clickCollect ? (
          <p className="text-sm text-violet-800 font-medium" data-testid="shipping-resolution-outcome">Click &amp; Collect — no carrier, no shipment, no label.</p>
        ) : resolved ? (
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm" data-testid="shipping-resolution-outcome">
            <Kv k="Carrier" v={res.carrier} strong />
            <Kv k="Service" v={res.carrier_service} strong />
            <Kv k="Matched rule" v={res.rule_name} />
            <Kv k="Auto label" v={res.auto_label ? "Eligible" : "No"} />
          </div>
        ) : (
          <p className="text-sm text-amber-800 font-medium" data-testid="shipping-resolution-outcome">{res.reason || "No enabled shipping rule matched this order."}</p>
        )}
      </div>

      {!clickCollect && (
        <div className="flex items-center gap-2 mb-4 text-sm" data-testid="order-weight-control">
          <span className="text-slate-500 font-medium">Parcel weight</span>
          <select
            value={customOpen ? "custom" : (f.manual_parcel_weight_kg ?? "")}
            onChange={(e) => { if (e.target.value === "custom") { setCustomOpen(true); } else { setCustomOpen(false); setWeight(e.target.value === "" ? null : Number(e.target.value)); } }}
            disabled={!canManage || busy === "weight"}
            className="border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white disabled:opacity-60"
            data-testid="order-weight-select"
          >
            <option value="">{f.parcel_weight_kg != null ? `Auto (${f.parcel_weight_kg} kg)` : "Not set"}</option>
            {WEIGHT_OPTIONS.map((w) => <option key={w} value={w}>{w} kg</option>)}
            <option value="custom">Custom…</option>
          </select>
          {customOpen && (
            <span className="inline-flex items-center gap-1">
              <input type="number" step="0.01" min="0" value={customW} onChange={(e) => setCustomW(e.target.value)} placeholder="kg" className="w-20 border border-slate-200 rounded-lg px-2 py-1.5" data-testid="order-weight-custom" />
              <button onClick={() => { if (customW) { setWeight(Number(customW)); setCustomOpen(false); } }} disabled={busy === "weight"} className="text-xs font-semibold bg-indigo-600 text-white px-2.5 py-1.5 rounded-lg" data-testid="order-weight-custom-set">Set</button>
            </span>
          )}
          {f.manual_parcel_weight_kg != null && !customOpen && <span className="text-xs font-semibold text-indigo-600">manual</span>}
          {busy === "weight" && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        </div>
      )}

      {/* Royal Mail panel (only when a shipment can be created) */}
      {resolved && (
        <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-4 mb-4" data-testid="royalmail-panel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-indigo-900 flex items-center gap-1.5"><Send className="w-4 h-4" /> Royal Mail Click &amp; Drop</h3>
            {hasRmOrder && <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> Order created</span>}
          </div>

          {!hasRmOrder ? (
            <div>
              <p className="text-xs text-slate-500 mb-3">
                {shipment?.status === "create_failed" ? <span className="text-red-600">Last attempt failed: {shipment.error}</span> : "One consignment, one or more parcels. Extra parcels are charged within the same consignment. Labels are pulled manually — nothing is charged until you pull a label."}
              </p>
              {canManage && (
                <div className="space-y-2 mb-3" data-testid="rm-parcels-editor">
                  {parcels.map((p, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-2 bg-white border border-slate-200 rounded-lg p-2" data-testid={`rm-parcel-row-${i}`}>
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-indigo-700 w-12"><Package className="w-3.5 h-3.5" /> #{i + 1}</span>
                      <label className="text-xs text-slate-500">Weight
                        <input type="number" step="0.01" min="0" value={p.weight_kg} onChange={(e) => updateParcel(i, "weight_kg", e.target.value)} placeholder="kg" className="ml-1 w-20 border border-slate-200 rounded px-2 py-1" data-testid={`rm-parcel-weight-${i}`} /></label>
                      <label className="text-xs text-slate-400">L<input type="number" min="0" value={p.length} onChange={(e) => updateParcel(i, "length", e.target.value)} placeholder="cm" className="ml-1 w-14 border border-slate-200 rounded px-2 py-1" /></label>
                      <label className="text-xs text-slate-400">W<input type="number" min="0" value={p.width} onChange={(e) => updateParcel(i, "width", e.target.value)} placeholder="cm" className="ml-1 w-14 border border-slate-200 rounded px-2 py-1" /></label>
                      <label className="text-xs text-slate-400">H<input type="number" min="0" value={p.height} onChange={(e) => updateParcel(i, "height", e.target.value)} placeholder="cm" className="ml-1 w-14 border border-slate-200 rounded px-2 py-1" /></label>
                      {parcels.length > 1 && (
                        <button onClick={() => removeParcel(i)} className="ml-auto text-slate-400 hover:text-red-600" title="Remove parcel" data-testid={`rm-parcel-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
                      )}
                    </div>
                  ))}
                  <div className="flex items-center justify-between">
                    <button onClick={addParcel} className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 hover:text-indigo-800" data-testid="rm-add-parcel-btn"><Plus className="w-4 h-4" /> Add parcel</button>
                    <span className="text-xs text-slate-500" data-testid="rm-parcels-summary">{parcels.length} parcel{parcels.length > 1 ? "s" : ""} · total {parcels.reduce((a, p) => a + (Number(p.weight_kg) || 0), 0).toFixed(2)} kg</span>
                  </div>
                </div>
              )}
              {canManage && (
                <div className="flex flex-wrap gap-2">
                <button onClick={checkRequest} disabled={busy} className="inline-flex items-center gap-1.5 border border-indigo-200 bg-white text-indigo-700 text-sm font-semibold px-3.5 py-2 rounded-lg disabled:opacity-60" data-testid="rm-check-request-btn">
                  {busy === "preview" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Check request
                </button>
                <button onClick={createOrder} disabled={busy} className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-3.5 py-2 rounded-lg disabled:opacity-60" data-testid="rm-create-order-btn">
                  {busy === "create" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Create Royal Mail order
                </button>
                </div>
              )}
              {preview && (
                <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3 text-xs" data-testid="rm-request-preview">
                  <div className="font-semibold text-slate-700">Will send: {preview.carrier} · service {preview.postageDetails?.serviceCode} (register {preview.postageDetails?.serviceRegisterCode})</div>
                  {preview.packages.map((p, i) => <div key={i} className="text-slate-600">Parcel {i + 1}: {p.packageFormatIdentifier} · {p.weightInGrams} g{p.dimensions ? ` · ${p.dimensions.depthInMms}×${p.dimensions.widthInMms}×${p.dimensions.heightInMms} mm` : ""}</div>)}
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm mb-3">
                <Kv k="RM order ID" v={shipment.rm_order_identifier} mono />
                <Kv k="Tracking no." v={shipment.tracking_number || "pending"} mono />
                <Kv k="Service" v={shipment.service_used} />
                <Kv k="Service code" v={`${shipment.service_code || "—"} / ${shipment.service_register_code || "—"}`} mono />
                <Kv k="Label status" v={shipment.label_status} />
                <Kv k="Parcels" v={shipment.num_parcels} strong />
                <Kv k="Total weight" v={shipment.total_weight_kg != null ? `${shipment.total_weight_kg} kg` : "—"} />
                <Kv k="Created by" v={shipment.created_by} />
              </div>
              {(shipment.parcels || []).length > 0 && (
                <div className="mb-3 rounded-lg border border-slate-100 bg-white divide-y divide-slate-50" data-testid="rm-parcels-list">
                  {shipment.parcels.map((p, i) => (
                    <div key={i} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`rm-parcel-view-${i}`}>
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-indigo-700"><Package className="w-3.5 h-3.5" /> #{p.parcel_number}</span>
                      <span className="text-slate-600">{p.weight_kg} kg</span>
                      <span className="text-slate-400 text-xs">{(p.length_cm && p.width_cm && p.height_cm) ? `${p.length_cm}×${p.width_cm}×${p.height_cm} cm` : "—"}</span>
                      <span className="font-mono text-xs text-slate-500 truncate">{p.tracking_number || "—"}</span>
                      {labelStored && (
                        <button onClick={() => printParcel(p.parcel_number)} disabled={busy} className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800" data-testid={`rm-parcel-print-${i}`}><Printer className="w-3.5 h-3.5" /> Label</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                {!labelStored && canManage && (
                  <button onClick={pullLabel} disabled={busy} className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-3.5 py-2 rounded-lg disabled:opacity-60" data-testid="rm-pull-label-btn">
                    {busy === "label" ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackageCheck className="w-4 h-4" />} Pull label
                  </button>
                )}
                {labelStored && (<>
                  <button onClick={viewLabel} disabled={busy} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold px-3 py-2 rounded-lg" data-testid="rm-view-label-btn"><Eye className="w-4 h-4" /> View</button>
                  <button onClick={printLabel} disabled={busy} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold px-3 py-2 rounded-lg" data-testid="rm-print-label-btn"><Printer className="w-4 h-4" /> Print all labels</button>
                  <button onClick={downloadLabel} disabled={busy} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold px-3 py-2 rounded-lg" data-testid="rm-download-label-btn"><FileDown className="w-4 h-4" /> Download</button>
                  <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-medium ml-1"><CheckCircle2 className="w-3.5 h-3.5" /> Label stored</span>
                </>)}
              </div>
              {shipment.error && <p className="text-xs text-red-600 mt-2">{shipment.error}{shipment.http_status ? ` (HTTP ${shipment.http_status})` : ""}</p>}
            </>
          )}
        </div>
      )}

      {/* Fulfilment snapshot */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm">
        <Kv k="WC method" v={f.shipping_method_title || f.shipping_method_id} />
        <Kv k="Zone" v={f.zone} />
        <Kv k="Zone source" v={ZONE_SOURCE_LABEL[f.zone_source] || f.zone_source} />
        <Kv k="Postcode" v={f.postcode} mono />
        <Kv k="Parcel weight" v={f.parcel_weight_kg != null ? `${f.parcel_weight_kg} kg` : "—"} />
        <Kv k="Parcel dims" v={dims(f.parcel_dimensions_cm)} />
        <Kv k="Shipping charged" v={f.shipping_amount != null ? `£${Number(f.shipping_amount).toFixed(2)}` : "—"} />
        <Kv k="Order value" v={f.order_value != null ? `£${Number(f.order_value).toFixed(2)}` : "—"} />
        <Kv k="Recipient" v={f.recipient_name} />
      </div>
    </section>
  );
};

export default ShippingResolutionCard;
